import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

interface Props {
    contactId: number;
    contactName: string;
    open: boolean;
    onClose: () => void;
    onDraftCreated: () => void;
}

export function DraftOutreachDialog({
    contactId,
    contactName,
    open,
    onClose,
    onDraftCreated,
}: Props) {
    const [channel, setChannel] = useState<"linkedin" | "email">("linkedin");
    const [language, setLanguage] = useState("de");
    const [customInstructions, setCustomInstructions] = useState("");
    const [draftContent, setDraftContent] = useState("");
    const [status, setStatus] = useState<
        "config" | "generating" | "editing" | "error"
    >("config");
    const [error, setError] = useState("");
    const [regenerateCount, setRegenerateCount] = useState(0);

    const generate = async () => {
        setStatus("generating");
        setError("");
        try {
            const { data, error: fnError } = await supabase.functions.invoke(
                "draft-outreach",
                {
                    body: {
                        contact_id: contactId,
                        channel,
                        language,
                        custom_instructions: customInstructions || undefined,
                    },
                },
            );
            if (fnError) throw new Error(fnError.message);
            setDraftContent(data.draft_content);
            setStatus("editing");
            setRegenerateCount((c) => c + 1);
        } catch (err: unknown) {
            setError(
                err instanceof Error ? err.message : "Failed to generate draft",
            );
            setStatus("error");
        }
    };

    const handleApprove = async () => {
        // Find the latest draft for this contact and update its status
        const { data: drafts } = await supabase
            .from("outreach_drafts")
            .select("id")
            .eq("contact_id", contactId)
            .order("created_at", { ascending: false })
            .limit(1);
        if (drafts?.[0]) {
            await supabase
                .from("outreach_drafts")
                .update({ status: "approved", draft_content: draftContent })
                .eq("id", drafts[0].id);
        }
        onDraftCreated();
        onClose();
    };

    const handleCopyAndOpen = (type: "linkedin" | "email") => {
        navigator.clipboard.writeText(draftContent);
        if (type === "linkedin") {
            // Open contact's LinkedIn profile (would need linkedin_url from contact)
            alert("Draft copied to clipboard! Open LinkedIn to send.");
        }
    };

    return (
        <Dialog open={open} onOpenChange={() => onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        Draft Outreach — {contactName}
                    </DialogTitle>
                </DialogHeader>
                {status === "config" && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium">
                                    Channel
                                </label>
                                <Select
                                    value={channel}
                                    onValueChange={(v) =>
                                        setChannel(v as "linkedin" | "email")
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="linkedin">
                                            LinkedIn Message
                                        </SelectItem>
                                        <SelectItem value="email">
                                            Email
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium">
                                    Language
                                </label>
                                <Select
                                    value={language}
                                    onValueChange={setLanguage}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="de">
                                            Deutsch
                                        </SelectItem>
                                        <SelectItem value="en">
                                            English
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium">
                                Custom Instructions (optional)
                            </label>
                            <Textarea
                                value={customInstructions}
                                onChange={(e) =>
                                    setCustomInstructions(e.target.value)
                                }
                                placeholder="e.g., Mention the MedTech conference, keep it under 3 sentences..."
                                rows={3}
                            />
                        </div>
                        <Button onClick={generate} className="w-full">
                            Generate Draft
                        </Button>
                    </div>
                )}
                {status === "generating" && (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <p className="text-muted-foreground">
                            Generating personalized draft...
                        </p>
                    </div>
                )}
                {status === "editing" && (
                    <div className="space-y-4">
                        <Textarea
                            value={draftContent}
                            onChange={(e) => setDraftContent(e.target.value)}
                            rows={10}
                            className="font-mono text-sm"
                        />
                        <div className="flex gap-2">
                            <Button onClick={handleApprove}>Approve</Button>
                            <Button
                                variant="outline"
                                onClick={generate}
                                disabled={regenerateCount >= 3}
                            >
                                Regenerate ({3 - regenerateCount} left)
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => handleCopyAndOpen(channel)}
                            >
                                Copy &amp; Open{" "}
                                {channel === "linkedin" ? "LinkedIn" : "Email"}
                            </Button>
                            <Button variant="ghost" onClick={onClose}>
                                Discard
                            </Button>
                        </div>
                    </div>
                )}
                {status === "error" && (
                    <div className="space-y-3">
                        <p className="text-sm text-destructive">{error}</p>
                        <div className="flex gap-2">
                            <Button onClick={generate}>Try Again</Button>
                            <Button variant="ghost" onClick={onClose}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
